import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { AddToBatch } from './add-to-batch';
import { ShipToAddress, ShipToLabel } from './address';
import { ReadOnlyAmountSummary } from './amount-summary/read-only';
import { AmountSummaryLabel } from './amount-summary/read-only/amount-summary-label';
import { ShipmentDetailsField } from './field';
import { GiftCheckbox } from './gift-checkbox/gift-checkbox';
import messages, { errorMessages } from './messages';
import { ReadOnlyRecipientTaxIdentifiers } from './tax-identifier/read-only-tax-identifiers';
import AssignUserDropdown from '../../../../../components/assign-user-dropdown';
import { emptyObject } from 'ui-components/default-props/empty';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { updateFulfillmentPlansAssignedTo } from '../../../../../fulfillment-plans/fulfillment-plans.api';
import { useEntitiesFulfillmentPlan } from '../../../../../hooks/entities/selectors/fulfillment-plans';
import * as Actions from '../../../../../store/entities/entities.actions';
import { processEntities } from '../../../../../store/entities/entities.bulkload';
import { ShipmentDetailsLayoutLoader } from '../../../layouts/shipment-details-layout/shipment-details-layout-loader';
import { ShipByDate } from './dates/ship-by-date';
import { DeliverByDate } from './dates/deliver-by-date';
import { HoldUntilDate } from './dates/hold-until-date';
import { PaidDate } from './dates/paid-date';
import { useEnableDeliverByDate } from '../../../../../feature-flags/hooks/use-enable-deliver-by-date';
import { useContainsDemoOrder } from '../../../../../demos/hooks/use-contains-demo-order';

const AssignUserFieldId = 'assign-user';
const AddToBatchFieldId = 'add-to-batch';

const showErrorNotification = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...errorMessages.errorNotificationTitle} />,
    content: <FormattedMessage {...errorMessages.errorNotificationBody} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

const ShipmentDetailsSectionContent = () => {
  const dispatch = useDispatch();
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const enableDeliverByDate = useEnableDeliverByDate();
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);

  const { shipTo = emptyObject, recipientInfo = emptyObject, lockAddress } = useEntitiesFulfillmentPlan(fulfillmentPlanId) || emptyObject;

  const onAssignToChange = useCallback(
    async (userId) => {
      try {
        const response = await updateFulfillmentPlansAssignedTo([fulfillmentPlanId], userId);
        const { data: updatedEntityFulfillmentPlans } = response;
        dispatch(
          Actions.entitiesBulkload(
            processEntities({
              fulfillmentPlans: updatedEntityFulfillmentPlans,
            }),
          ),
        );
      } catch {
        showErrorNotification();
      }
    },
    [dispatch, fulfillmentPlanId],
  );

  const addressColumnContent = (
    <>
      <ShipmentDetailsField label={<ShipToLabel isLocked={lockAddress} />}>
        <ShipToAddress recipientInfo={recipientInfo} shipTo={shipTo} />
      </ShipmentDetailsField>
      <GiftCheckbox showErrorNotification={showErrorNotification} />
    </>
  );

  const costSummaryColumnContent = (
    <>
      <ShipmentDetailsField label={<AmountSummaryLabel />}>
        <ReadOnlyAmountSummary />
      </ShipmentDetailsField>
      <ShipmentDetailsField label={<FormattedMessage {...messages.recipientTaxInformationLabel} />}>
        <ReadOnlyRecipientTaxIdentifiers fulfillmentPlanId={fulfillmentPlanId} />
      </ShipmentDetailsField>
    </>
  );

  const datesColumnContent = (
    <>
      {enableDeliverByDate && <DeliverByDate />}
      <ShipByDate />
      {!isDemoOrder && <HoldUntilDate />}
      <PaidDate />
    </>
  );

  const assignmentColumnContent = (
    <>
      <ShipmentDetailsField label={<FormattedMessage {...messages.assignedTo} />} labelFor={AssignUserFieldId}>
        <AssignUserDropdown fulfillmentPlanId={fulfillmentPlanId} id={AssignUserFieldId} isDisabled={false} onChange={onAssignToChange} />
      </ShipmentDetailsField>
      {!isDemoOrder && (
        <ShipmentDetailsField label={<FormattedMessage {...messages.batchLabel} />} labelFor={AddToBatchFieldId}>
          <AddToBatch fulfillmentPlanId={fulfillmentPlanId} id={AddToBatchFieldId} />
        </ShipmentDetailsField>
      )}
    </>
  );

  return (
    <ShipmentDetailsLayoutLoader
      addressColumnContent={addressColumnContent}
      assignmentColumnContent={assignmentColumnContent}
      costSummaryColumnContent={costSummaryColumnContent}
      datesColumnContent={datesColumnContent}
    />
  );
};

export default ShipmentDetailsSectionContent;
